import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary class to catch and handle rendering errors in the component tree.
 */
// FIX: Changed from React.Component to direct Component extension to resolve 'props' type recognition issues in some TypeScript environments
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      const isScriptError = this.state.error?.message === 'Script error.';
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-stone-200 p-8 text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} />
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2">Đã xảy ra lỗi!</h2>
            <p className="text-stone-500 text-sm mb-6">
              {isScriptError 
                ? "Ứng dụng gặp lỗi khi tải script (thường do AdBlock hoặc mạng). Vui lòng tắt chặn quảng cáo hoặc tải lại trang."
                : "Ứng dụng gặp sự cố không mong muốn. Vui lòng thử tải lại trang."}
            </p>
            
            {this.state.error && (
              <div className="bg-stone-900 rounded-xl p-4 mb-6 text-left overflow-auto max-h-40">
                <code className="text-xs text-red-300 font-mono break-words">
                  {isScriptError ? 'Script Error (CORS/Network Issue)' : this.state.error.toString()}
                </code>
              </div>
            )}

            <button 
              onClick={this.handleReload}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} /> Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    // FIX: Accessing children from this.props now correctly typed via inheritance from Component
    return this.props.children;
  }
}

export default ErrorBoundary;
